import matter from "gray-matter";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Buffer } from "buffer";

window.Buffer = Buffer; // ← ensure Buffer is available globally

export function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const blogFiles = ["The_Future_Of_AI_Agents.md"];

        const blogData = await Promise.all(
          blogFiles.map(async (filename) => {
            try {
              const response = await fetch(`/markdowns/${filename}`);
              const content = await response.text();
              console.log(`Loaded ${filename}`);
              const { data, content: body } = matter(content);
              return {
                slug: filename.replace(".md", ""),
                title: data.title || "Untitled",
                date: data.date || new Date().toISOString(),
                excerpt: data.excerpt || body.substring(0, 200) + "...",
                ...data,
              };
            } catch (error) {
              console.error(`Error loading ${filename}:`, error);
              return null;
            }
          }),
        );

        setBlogs(
          blogData
            .filter(Boolean)
            .sort((a, b) => new Date(b.date) - new Date(a.date)),
        );
      } catch (error) {
        console.error("Error loading blogs:", error);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, []);

  const containerStyle = {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    minHeight: "100vh",
    background: "#fafafa",
    color: "#1a1a2e",
    padding: isMobile ? "2rem 1rem" : "4rem 2rem",
  };

  const headerStyle = {
    maxWidth: "960px",
    margin: "0 auto 4rem",
    textAlign: "center",
  };

  const blogListStyle = {
    maxWidth: "960px",
    margin: "0 auto",
  };

  const blogCardStyle = {
    background: "#fff",
    borderRadius: "1rem",
    padding: isMobile ? "2rem" : "3rem",
    marginBottom: "2rem",
    border: "1px solid rgba(26,26,46,0.08)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "pointer",
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: "center", marginTop: "4rem" }}>
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: "1.25rem",
              color: "rgba(26,26,46,0.6)",
            }}
          >
            Loading blogs...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <Link
          to="/"
          style={{
            position: "absolute",
            top: "2rem",
            left: "2rem",
            padding: "0.55rem 1rem",
            border: "1px solid #1a1a2e",
            borderRadius: "0.5rem",
            background: "transparent",
            color: "#1a1a2e",
            textDecoration: "none",
            fontFamily: "Inter, sans-serif",
            fontWeight: 500,
            fontSize: "0.875rem",
          }}
        >
          ← Home
        </Link>

        <h1
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: isMobile ? "3rem" : "4rem",
            fontWeight: 800,
            letterSpacing: "-0.03em",
            margin: "2rem 0 1rem",
            color: "#1a1a2e",
          }}
        >
          Blog
        </h1>
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: isMobile ? "1.125rem" : "1.25rem",
            color: "rgba(26,26,46,0.6)",
            lineHeight: 1.6,
          }}
        >
          Insights on AI and AI Agents
        </p>
      </div>

      <div style={blogListStyle}>
        {blogs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "4rem 0" }}>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "1.25rem",
                color: "rgba(26,26,46,0.5)",
              }}
            >
              No blog posts found.
            </p>
          </div>
        ) : (
          blogs.map((blog) => (
            <Link
              key={blog.slug}
              to={`/blog/${blog.slug}`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <div
                style={blogCardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow =
                    "0 12px 30px rgba(0,0,0,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 12px rgba(0,0,0,0.04)";
                }}
              >
                <h2
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: isMobile ? "1.5rem" : "2rem",
                    fontWeight: 700,
                    margin: "0 0 1rem",
                    color: "#1a1a2e",
                    lineHeight: 1.2,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {blog.title}
                </h2>
                <p
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.875rem",
                    color: "rgba(26,26,46,0.45)",
                    margin: "0 0 1rem",
                  }}
                >
                  {new Date(blog.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
                <p
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: isMobile ? "1rem" : "1.125rem",
                    lineHeight: 1.6,
                    margin: 0,
                    color: "rgba(26,26,46,0.6)",
                  }}
                >
                  {blog.excerpt}
                </p>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
